import React, { useState, useEffect, useRef } from 'react';
import { Box, LinearProgress, Typography, Alert, TextField, Button } from '@mui/material';
import { apiService, Request } from '../../services/apiService';
import { RequestDataSet } from '../../types/dataSets';

interface ImportSwaggerDataSetsProps {
  projectId: number;
  collectionsCreated: number;
  requestsCreated: number;
  onComplete: (dataSetsCreated: number) => void;
  onError: (error: string) => void;
}

const ImportSwaggerDataSets: React.FC<ImportSwaggerDataSetsProps> = ({
  projectId,
  collectionsCreated,
  requestsCreated,
  onComplete,
  onError,
}) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string>('Initializing...');
  const [dataSetsCreated, setDataSetsCreated] = useState(0);
  const [currentRequest, setCurrentRequest] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [numberOfDataSets, setNumberOfDataSets] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(true);
  const isMountedRef = useRef(true);

  const handleGenerateDataSets = async () => {
    if (numberOfDataSets < 1 || numberOfDataSets > 100) {
      setError('Number of data sets must be between 1 and 100');
      return;
    }

    setShowConfig(false);
    setIsGenerating(true);
    setError(null);

    try {
      if (!projectId || projectId <= 0) {
        const errorMsg = 'Project ID is required';
        if (isMountedRef.current) {
          setError(errorMsg);
          onError(errorMsg);
        }
        return;
      }

      if (requestsCreated === 0) {
        if (isMountedRef.current) {
          setStatus('No new requests to create data sets for');
          setProgress(100);
          onComplete(0);
        }
        return;
      }

      if (isMountedRef.current) {
        setStatus('Fetching created requests...');
        setProgress(10);
      }

      // Fetch all collections for this project
      const collections = await apiService.getCollectionsByProject(projectId);
      
      if (!isMountedRef.current) return;
      
      if (collections.length === 0) {
        if (isMountedRef.current) {
          setStatus('No collections found');
          setProgress(100);
          onComplete(0);
        }
        return;
      }

      if (isMountedRef.current) {
        setStatus('Loading requests from collections...');
        setProgress(20);
      }

      // Fetch all requests from all collections
      const allRequests: Request[] = [];
      for (const collection of collections) {
        try {
          const requests = await apiService.getRequestsByCollection(collection.id);
          allRequests.push(...requests);
        } catch (err) {
          console.warn(`Failed to fetch requests for collection ${collection.id}:`, err);
        }
      }

      if (allRequests.length === 0) {
        if (isMountedRef.current) {
          setStatus('No requests found');
          setProgress(100);
          onComplete(0);
        }
        return;
      }

      // Create data sets for all requests (not just those with bodies)
      // This ensures every imported request has at least one data set for testing
      const requestsNeedingDataSets = allRequests;

      if (isMountedRef.current) {
        setStatus(`Creating ${numberOfDataSets} data set(s) for ${requestsNeedingDataSets.length} requests...`);
        setProgress(30);
      }

      let createdCount = 0;
      const totalRequests = requestsNeedingDataSets.length;

      // Create specified number of data sets for each request
      for (let i = 0; i < requestsNeedingDataSets.length; i++) {
        if (!isMountedRef.current) break;
        
        const request = requestsNeedingDataSets[i];
        try {
          if (!request || !request.id) {
            console.warn('Skipping invalid request:', request);
            continue;
          }

          if (isMountedRef.current) {
            setCurrentRequest(`Processing: ${request.method || 'UNKNOWN'} ${request.name || 'Unknown Request'}`);
          }

          // Check how many data sets already exist (POST/PUT get "Data Set 1" during import)
          const existingDataSets = await apiService.getDataSetsByRequest(request.id);
          
          if (!isMountedRef.current) break;
          
          const existingCount = existingDataSets.length;
          const toCreate = Math.max(0, numberOfDataSets - existingCount);
          if (toCreate === 0) {
            console.log(`Skipping request ${request.id} - already has ${existingCount} data set(s)`);
            if (isMountedRef.current) {
              const progressPercent = 30 + ((i + 1) / totalRequests) * 60;
              setProgress(progressPercent);
            }
            continue;
          }

          // Prepare the data set body from existing row or request template
          let dataSetBody = '{}';
          const templateBody = existingDataSets[0]?.body ?? request.body;
          if (templateBody && String(templateBody).trim() !== '' && templateBody !== 'null' && templateBody !== '{}') {
            dataSetBody = String(templateBody);
          }

          const templateHeaders =
            (existingDataSets[0]?.headers && typeof existingDataSets[0].headers === 'string')
              ? existingDataSets[0].headers
              : (request.headers && typeof request.headers === 'string')
                ? request.headers
                : '{}';

          // Create remaining data sets so each request reaches the selected count
          for (let j = 0; j < toCreate; j++) {
            if (!isMountedRef.current) break;

            const dataSetIndex = existingCount + j + 1;
            const dataSet: RequestDataSet = {
              requestId: request.id,
              name: `Data Set ${dataSetIndex}`,
              description: `Data set ${dataSetIndex} for ${request.method} ${request.name}`,
              body: dataSetBody,
              headers: templateHeaders,
              queryParams: existingDataSets[0]?.queryParams ?? '{}',
              pathParams: existingDataSets[0]?.pathParams ?? '{}',
              urlTemplate: existingDataSets[0]?.urlTemplate ?? request.url ?? '',
              requestMethod: existingDataSets[0]?.requestMethod ?? request.method ?? 'GET',
            };

            await apiService.createDataSet(dataSet);
            createdCount++;
            
            if (isMountedRef.current) {
              setDataSetsCreated(createdCount);
            }
          }
          
          if (isMountedRef.current) {
            // Update progress
            const progressPercent = 30 + ((i + 1) / totalRequests) * 60;
            setProgress(progressPercent);
          }
        } catch (err: any) {
          console.error(`Error creating data set for request ${request?.id}:`, err);
          // Continue with next request instead of failing completely
          if (!isMountedRef.current) break;
        }
      }

      if (isMountedRef.current) {
        setStatus(`Successfully created ${createdCount} data set(s)`);
        setProgress(100);
        setCurrentRequest('');
        setIsGenerating(false);
        onComplete(createdCount);
      }
    } catch (err: any) {
      console.error('Error in createDataSetsForRequests:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create data sets';
      if (isMountedRef.current) {
        setError(errorMessage);
        setStatus('Error creating data sets');
        setIsGenerating(false);
        onError(errorMessage);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 700 }}>
        Creating Data Sets
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showConfig && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1.5, fontSize: '0.875rem' }}>
            Specify the number of data sets to create for each request:
          </Typography>
          <TextField
            type="number"
            size="small"
            label="Number of Data Sets per Request"
            value={numberOfDataSets}
            onChange={(e) => setNumberOfDataSets(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 100 }}
            fullWidth
            sx={{ mb: 2 }}
            helperText={`This will create ${numberOfDataSets} data set${numberOfDataSets > 1 ? 's' : ''} for each of the ${requestsCreated} imported request${requestsCreated > 1 ? 's' : ''} (Total: ${numberOfDataSets * requestsCreated} data sets)`}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateDataSets}
            disabled={isGenerating}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              height: 36,
              minHeight: 36,
              fontSize: '0.875rem',
              bgcolor: '#1d4ed8',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1e40af' },
            }}
          >
            Generate Data Sets
          </Button>
        </Box>
      )}

      {!showConfig && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {status}
            </Typography>
            {currentRequest && (
              <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                {currentRequest}
              </Typography>
            )}
            {dataSetsCreated > 0 && (
              <Typography variant="body2" color="success.main">
                Created: {dataSetsCreated} data set(s)
              </Typography>
            )}
          </Box>

          <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
          
          <Typography variant="caption" color="text.secondary">
            {Math.round(progress)}% complete
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ImportSwaggerDataSets;

